import React, { useEffect, useMemo } from 'react';

import Polling from 'handshq-app/utilities/polling';
import DownloadIcon from 'icons/ic-download.svg';
import { useTranslation } from 'react-i18next';

import useDownloadPolling from 'components/hooks/useDownloadPolling';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import DownloadModalWrapper from 'components/application/DownloadModalWrapper';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectActivityContext } from 'components/contexts/ProjectActivityContext';
import ActivityTableData from 'components/projectActivity/components/ActivityTableData';

export default function ProjectActivityRow({ history }) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
    user: currentUser,
  } = useCurrentActor();

  const { isVersionPdfsEnabled, projectId, projectsDownloadPolicy } =
    useProjectActivityContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectActivity.components.ProjectActivityRow',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const statusPoller = useMemo(
    () =>
      new Polling({
        interval: 1000,
        url: `/versions/${history.versionId}`,
        responseCheck(response) {
          return response.data.relationships.versionPdf.data !== null;
        },
        successCallback: (response) => {
          setDownloadState({
            ...downloadState,
            downloadFailed: false,
            downloadInProgress: false,
            downloadSucceeded: true,
            downloadLink: response.included[0].links.content,
          });
        },
        failureCallback: (_response) => {
          setDownloadState({
            ...downloadState,
            downloadInProgress: false,
            downloadFailed: true,
          });
        },
      }),
    [],
  );

  const [
    onClick,
    startDownload,
    resetAction,
    closeModal,
    isModalOpen,
    downloadState,
    setDownloadState,
  ] = useDownloadPolling(statusPoller);

  const downloadVersion = () => {
    window.open(downloadState.downloadLink, '_blank');
  };

  useEffect(
    () => () => {
      statusPoller && statusPoller.end();
    },
    [],
  );

  return (
    <tr className='handshq__tr--taller hover:tw-bg-grey-025'>
      <ActivityTableData history={history} />
      {isVersionPdfsEnabled && history.versionId && (
        <td className='handshq__td-button-cell--align-right tw-border-grey-100'>
          <ConditionalWrapper
            condition={!projectsDownloadPolicy.canShow}
            wrapper={(children) => (
              <Tooltip
                placement='top'
                tooltip={i18nT('disabledTooltip', {
                  interpolation: { escapeValue: false },
                  singularRAMSName,
                })}
                trigger='hover'
              >
                {children}
              </Tooltip>
            )}
          >
            <OutlinedButton
              color='grey'
              disabled={!projectsDownloadPolicy.canShow}
              onClick={onClick}
              size='sm'
            >
              <DownloadIcon className='-tw-ml-2' height={24} width={24} />
              <span>{i18nTShared('download')}</span>
            </OutlinedButton>
          </ConditionalWrapper>
          {isModalOpen && (
            <DownloadModalWrapper
              analyticProps={{
                currentUser,
                projectId,
                versionId: history.versionId,
              }}
              analyticTrackingId='Project history version downloaded'
              closeModal={closeModal}
              downloadAction={downloadVersion}
              downloadStatus={downloadState}
              downloadingBodyText={i18nTShared('downloading...')}
              isLandscape={true}
              modalIsOpen={true}
              resetAction={resetAction}
              retryAction={startDownload}
            />
          )}
        </td>
      )}
    </tr>
  );
}
